import type {
  AttachmentApi,
  FileMetadataApi,
  ImageMetadataApi,
  TextEntityApi,
  VideoMetadataApi,
} from 'api-typings/common/common';
import type { PostApi } from 'api-typings/posts/posts';
import { transformRichTextElement } from '../content-customization/content-customization.transformers';
import { transformPostFile } from '../post-files';
import type {
  Attachment,
  FileImageMetadata,
  FileMetadata,
  FileVideoMetadata,
  Post,
  TextEntity,
} from './feed-posts.model';

export function transformFeedPost(post: PostApi): Post {
  return {
    attachments: transformPostAttachments(post.attachments),
    attachmentFiles: post.attachment_files.map(file => transformPostFile(file)),
    commentingDisabled: post.commenting_disabled,
    content: post.content || '',
    created: post.created,
    textEntities: post.text_entities.map(transformTextEntity),
    isDeleted: post.is_deleted,
    ownerId: post.owner_id,
    ownerType: post.owner_type,
    updated: post.updated,
    deleted: post.deleted ? post.deleted : undefined,
    id: post.id,
    commentFeedId: post.comment_feed_id,
    contentRichText: post.content_rich_text?.map(transformRichTextElement),
  };
}

/**
 * Transforms a list of attachments from the API into a list of attachments
 * If at least one attachment has a position, sort the attachments by position
 * @param attachmentApis
 */
function transformPostAttachments(
  attachmentApis: readonly AttachmentApi[],
): readonly Attachment[] {
  const attachments = attachmentApis.map(transformPostAttachment);
  return attachments.sort((a, b) => a.position - b.position);
}

function transformPostAttachment(attachment: AttachmentApi): Attachment {
  return {
    id: attachment.id,
    attachedToType: attachment.attached_to_type,
    attachedToId: attachment.attached_to_id,
    attachedObjectType: attachment.attached_object_type,
    attachedObjectId: attachment.attached_object_id,
    attachedByType: attachment.attached_by_type,
    attachedById: attachment.attached_by_id,
    attachmentUuid: attachment.attachment_uuid,
    position: attachment.position ?? -1,
    created: attachment.created,
    metadata: transformFileMetadata(attachment.file_metadata),
  };
}

export function transformFileMetadata(
  fileMetadata?: FileMetadataApi,
): FileMetadata | undefined {
  if (!fileMetadata) {
    return undefined;
  }

  if (fileMetadata.metadata?.image) {
    return {
      image: transformImageMetadata(fileMetadata.metadata.image),
    };
  }

  if (fileMetadata.metadata?.video) {
    return {
      video: transformVideoMetadata(fileMetadata.metadata.video),
    };
  }

  return undefined;
}

function transformImageMetadata(
  image?: ImageMetadataApi,
): FileImageMetadata | undefined {
  if (!image) return undefined;

  if (image.width || image.height || image.blur_hash) {
    return {
      width: image.width,
      height: image.height,
      blurHash: image.blur_hash,
    };
  }

  return undefined;
}

function transformVideoMetadata(
  video?: VideoMetadataApi,
): FileVideoMetadata | undefined {
  if (!video) {
    return undefined;
  }

  if (video.width && video.height) {
    return {
      width: video.width,
      height: video.height,
    };
  }

  return undefined;
}

export function transformTextEntity(textEntityApi: TextEntityApi): TextEntity {
  return {
    id: textEntityApi.id,
    offset: textEntityApi.offset,
    length: textEntityApi.length,
    referenceType: textEntityApi.reference_type,
    referenceId: textEntityApi.reference_id,
    contextType: textEntityApi.context_type,
    contextId: textEntityApi.context_id,
  };
}
